// 22.01.19 - This reducer controls all the states for the app. It specifies how the app's state changes in response to actions sent to the store. Takes the previous state and returns the new state.
import { Reducer } from "redux";
import { ActionTypes } from "../constants/actions";
import {
  ICase,
  IUserDetail,
  IIssueType,
  ICountry,
  IssueType,
  IClaimType,
  IClaimStatus,
  IGrantType,
} from "src/constants/api";

// This function converts an array to a map. Use for cases, countries and master tables.
function normalizeArray<T, K extends keyof T>(
  array: T[],
  indexKey: K
): ReadonlyMap<T[K], T> {
  const mapFn: (x: T) => [T[K], T] = (x) => [x[indexKey], x];
  const entries: ReadonlyArray<[T[K], T]> = array.map(mapFn);
  const m: ReadonlyMap<T[K], T> = new Map(entries);
  return m;
}

// This interface describes the structure of the state of all the data in the app. The ones withn the ? are optional and can be undefined.
export interface IDetailState {
  // caseData?: ReadonlyMap<string | undefined, ICase>;
  caseData?: ICase;
  currentUser?: IUserDetail;
  caseSaveInProgress: boolean;
  countries?: ReadonlyMap<number | string, ICountry>;
  issueTypes?: ReadonlyMap<IssueType, IIssueType>;
  claimTypes?: ReadonlyMap<number, IClaimType>;
  claimStatus?: ReadonlyMap<number, IClaimStatus>;
  grantTypes?: ReadonlyMap<number, IGrantType>;
  expectedLossCurrency?: string;
  gridData?: any;
  attachmentData?: any;
  attachmentDataShipment?: any;
  attachmentDataShipmentInventory?: any;
  attachmentDataQmu?: any;
  attachmentDataInventory?: any;
  attachmentDataQmuOer?: any;
  allowEdit: boolean;
  solutionOwner?: any;
  solutionTeam?: any;
  isLockForEdit: any;
  countriesData: any;
  currentUsername: string;
  isCurrentUserSO: boolean;
  isCurrentUserST: boolean;
  isCheckOutByDiffUser: boolean;
}

// This is the default state when the app starts.
const initialState: IDetailState = {
  caseSaveInProgress: false,
  currentUser: undefined,
  allowEdit: true,
  isLockForEdit: null,
  countriesData: [],
  currentUsername: "",
  isCurrentUserSO: false,
  isCurrentUserST: false,
  isCheckOutByDiffUser: false,
};

// All the state updates takes place here.
export const detailReducer: Reducer<IDetailState> = (
  // This will be called when the app loads and no state is passed.
  state: IDetailState = initialState,
  action: any
) => {
  switch (action.type) {
    case ActionTypes.DETAIL.SAVE_CASE: // These 2 actions executes the same code below.
    case ActionTypes.DETAIL.UPDATE_CASE:
      // We set this boolean to keep track of when we are saving/updating a case
      // we set it to true when saving or updating starts, and below we set it to false
      // when it has finished successfully or failed
      return {
        ...state,
        caseSaveInProgress: true,
      };
    case ActionTypes.DETAIL.SAVE_CASE_SUCCEEDED:
    case ActionTypes.DETAIL.SAVE_CASE_FAILED: {
      return {
        ...state,
        caseSaveInProgress: false,
      };
    }
    case ActionTypes.DETAIL.FETCH_CASE_SUCCEEDED: {
      // This reducer lets us fetch a single case, and ensures that the cases is just added to the already
      // fetched cases if there are any
      //   const oldEntries: ReadonlyArray<
      //     [string | undefined, ICase]
      //   > = state.caseData ? Array.from(state.caseData.entries()) : []; // These are all the cases we already have.
      //   const concatted: ReadonlyArray<
      //     [string | undefined, ICase]
      //   > = oldEntries.concat([[action.payload.uniqueId, action.payload]]); // These are all the cases and the new case.
      //   const newMap: ReadonlyMap<string | undefined, ICase> = new Map(concatted); // This is now the new mapp with all the cases.
      return {
        ...state,
        caseData: action.payload || {},
      };
    }
    case ActionTypes.DETAIL.ISSUE_TYPES_RECEIVED: // Master table data received.
      return {
        ...state,
        // issueTypes: normalizeArray(action.payload, "id")
        issueTypes: action.payload || [],
      };
    case ActionTypes.DETAIL.COUNTRIES_RECEIVED: // Master table data received.
      return {
        ...state,
        countries: normalizeArray(action.payload, "id"),
        countriesData: action.payload || [],
      };
    case ActionTypes.DETAIL.GRANT_TYPES_RECEIVED: {
      // Master table data received.
      return {
        ...state,
        // grantTypes: normalizeArray(action.payload, "id")
        grantTypes: action.payload || [],
      };
    }
    case ActionTypes.DETAIL.CLAIM_TYPES_RECEIVED: {
      // Master table data received.
      return {
        ...state,
        // claimTypes: normalizeArray(action.payload, "id")
        claimTypes: action.payload || [],
      };
    }

    case ActionTypes.DETAIL.CLAIM_STATUS_RECEIVED: {
      // Master table data received.
      return {
        ...state,
        // claimStatus: normalizeArray(action.payload, "id")
        claimStatus: action.payload || [],
      };
    }

    case ActionTypes.DETAIL.CLAIM_STATUS_RECEIVED: {
      // Master table data received.
      return {
        ...state,
        // claimStatus: normalizeArray(action.payload, "id")
        claimStatus: action.payload || [],
      };
    }

    case ActionTypes.DETAIL.SET_ATTACHMENT_DATA:
      return {
        ...state,
        attachmentData: action.payload.data,
      };

    case ActionTypes.DETAIL.SET_ATTACHMENT_DATA_SHIPMENT:
      return {
        ...state,
        attachmentDataShipment: action.payload.data,
      };

    case ActionTypes.DETAIL.SET_ATTACHMENT_DATA_SHIPMENT_INVENTORY:
      return {
        ...state,
        attachmentDataShipmentInventory: action.payload.data,
      };

    case ActionTypes.DETAIL.SET_ATTACHMENT_DATA_QMU:
      return {
        ...state,
        attachmentDataQmu: action.payload.data,
      };

    case ActionTypes.DETAIL.SET_ATTACHMENT_DATA_INVENTORY:
      return {
        ...state,
        attachmentDataInventory: action.payload.data,
      };

    case ActionTypes.DETAIL.SET_ATTACHMENT_DATA_QMU_OER:
      return {
        ...state,
        attachmentDataQmuOer: action.payload.data,
      };

    case ActionTypes.DETAIL.ALLOW_EDIT:
      return {
        ...state,
        allowEdit: action.payload,
      };

    case ActionTypes.DETAIL.IS_LOCK_FOR_EDIT:
      return {
        ...state,
        isLockForEdit: action.payload,
      };

    case ActionTypes.DETAIL.FETCH_SOLUTION_USERS_SUCCESS: {
      const solutionOwner = action.payload.filter(
        (value: any) => value.isSolutionOwner === true
      );
      const solutionTeam = action.payload.filter(
        (value: any) => value.isSolutionTeam === true
      );
      return {
        ...state,
        solutionOwner,
        solutionTeam,
      };
    }
    case ActionTypes.DETAIL.SET_CURRENT_USERNAME:
      return {
        ...state,
        currentUsername: action.payload,
      };

    case ActionTypes.DETAIL.IS_CURRENT_USER_SO:
      return {
        ...state,
        isCurrentUserSO: action.payload,
      };

    case ActionTypes.DETAIL.IS_CURRENT_USER_ST:
      return {
        ...state,
        isCurrentUserST: action.payload,
      };

    case ActionTypes.DETAIL.IS_CHECKOUT_BY_DIFF_USER:
      return {
        ...state,
        isCheckOutByDiffUser: action.payload,
      };

    default:
      return state;
  }
};
